import { saveAs } from "file-saver";
import { Content, TDocumentDefinitions } from "pdfmake/interfaces";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import {
  ABSENT_VALUE,
  DisplayStep,
  MAX_TREE_DEPTH,
  toDisplaySteps,
  toDisplayText,
} from "@/utils/DeviceReportDisplay";

/**
 * Takes one device report out of Office: as the JSON that was stored, or as a PDF to attach to a
 * ticket or a mail.
 *
 * **JSON is the archive format, PDF is the readable one.** The JSON download is the stored object
 * byte for byte in structure, so it can be diffed against the file the production rig wrote. The PDF
 * is a rendering of it and deliberately lossy in shape: the steps become a table and everything else
 * a flat list of paths, because the rig's payload nests differently with every hardware revision and
 * a layout that mirrored the nesting would break on the next one.
 *
 * Kept out of the viewer component so the file naming, the flattening and the document definition
 * are unit-testable without mounting anything.
 */

// pdfmake needs its font table before `createPdf`, exactly as in `PdfCreator.ts`. Assigning it twice
// across the two modules is harmless: it is the same object.
pdfMake.vfs = pdfFonts.pdfMake.vfs;

/** Everything the PDF header shows, resolved by the caller so this module needs no i18n. */
export interface DeviceReportPdfInfo {
  /**
   * Serial or MAC, exactly as the report carries it, and empty when it carries neither.
   *
   * Separate from `subjectLabel` on purpose: the label may be a translated placeholder, which would
   * otherwise end up in the file name ("Unbekanntes_Ger_t.pdf").
   */
  subject: string;
  /** What the header prints: the subject, or a placeholder for a report with no identifier. */
  subjectLabel: string;
  /** Capture time, already formatted in the user's locale. */
  capturedAtLabel: string;
  overallResult: string;
  reportId: string;
  labels: DeviceReportPdfLabels;
}

/** Translated headings, passed in so the util stays pure. */
export interface DeviceReportPdfLabels {
  title: string;
  steps: string;
  step: string;
  comment: string;
  status: string;
  detail: string;
  duration: string;
  rawData: string;
}

/** One line of the flattened payload. */
export interface FlatReportEntry {
  path: string;
  value: string;
}

/** Payload key rendered as its own table, so the flat list leaves it out. */
const STEPS_KEY = "steps";

/**
 * Characters a file name may carry.
 *
 * Everything else becomes an underscore: a report id is safe by construction, but the subject is a
 * serial or a MAC read from a report, and a MAC's colons are illegal in a Windows file name.
 */
const UNSAFE_FILE_NAME_CHARACTERS = /[^A-Za-z0-9._-]+/g;

/** Used when a report carries neither a subject nor an id, which only a hand-placed object would. */
const FALLBACK_FILE_NAME = "device-report";

/**
 * `BMK00383_20260817T132339Z_TEST_REPORT.json`.
 *
 * The subject comes first because a folder of these sorts by device, and the id already sorts
 * chronologically within one device (it is a UTC timestamp).
 */
export function buildDeviceReportFileName(
  subject: string,
  reportId: string,
  extension: string
): string {
  const parts = [subject, reportId]
    .map((part) => part.trim().replace(UNSAFE_FILE_NAME_CHARACTERS, "_"))
    .filter((part) => part.length > 0 && part !== "_");

  return `${parts.length > 0 ? parts.join("_") : FALLBACK_FILE_NAME}${extension}`;
}

/**
 * The payload as `path: value` lines, depth-capped like the on-screen tree.
 *
 * Nesting is expressed in the path (`firmware.version`, `captured_fields.ble_mac[1]`) rather than by
 * indentation, so a value can be searched for in the PDF and matched against the JSON. `steps` is
 * skipped at the root only: a nested key of that name elsewhere is data, not the steps table.
 */
export function toFlatReportEntries(payload: unknown, depth = 0, prefix = ""): FlatReportEntry[] {
  if (payload === null || typeof payload !== "object") {
    return [{ path: prefix, value: toDisplayText(payload) }];
  }

  if (depth >= MAX_TREE_DEPTH) {
    // The cap protects the renderer from an absurdly nested payload; saying so beats truncating
    // silently, since the JSON download still holds everything.
    return [{ path: prefix, value: "…" }];
  }

  const source = payload as Record<string, unknown>;
  const isArray = Array.isArray(payload);
  const keys = Object.keys(source).filter((key) => !(depth === 0 && key === STEPS_KEY));

  if (keys.length === 0) {
    return prefix ? [{ path: prefix, value: isArray ? "[]" : "{}" }] : [];
  }

  return keys.flatMap((key) => {
    const path = isArray
      ? `${prefix}[${Number(key) + 1}]`
      : prefix
        ? `${prefix}.${key}`
        : key;

    return toFlatReportEntries(source[key], depth + 1, path);
  });
}

/** The steps table, or nothing when the payload has no usable `steps`. */
function buildStepsContent(steps: DisplayStep[], labels: DeviceReportPdfLabels): Content[] {
  if (steps.length === 0) {
    return [];
  }

  return [
    { text: labels.steps, style: "sectionTitle" },
    {
      table: {
        headerRows: 1,
        // Fixed columns with the detail taking the slack: a step's detail is the one field that can
        // be a paragraph, and letting pdfmake size columns by content squeezes everything else.
        widths: [110, 120, 40, "*", 45],
        body: [
          [
            { text: labels.step, style: "tableHeader" },
            { text: labels.comment, style: "tableHeader" },
            { text: labels.status, style: "tableHeader" },
            { text: labels.detail, style: "tableHeader" },
            { text: labels.duration, style: "tableHeader", alignment: "right" },
          ],
          ...steps.map((step) => [
            step.step,
            step.comment,
            step.status || ABSENT_VALUE,
            step.detail,
            { text: step.duration, alignment: "right" },
          ]),
        ],
      },
      layout: "lightHorizontalLines",
      style: "table",
    },
  ];
}

/** The flattened payload as a two-column table. */
function buildRawDataContent(
  entries: FlatReportEntry[],
  labels: DeviceReportPdfLabels
): Content[] {
  if (entries.length === 0) {
    return [];
  }

  return [
    { text: labels.rawData, style: "sectionTitle" },
    {
      table: {
        widths: [200, "*"],
        body: entries.map((entry) => [{ text: entry.path, bold: true }, entry.value]),
      },
      layout: "noBorders",
      style: "table",
    },
  ];
}

/** The whole document, ready for `createPdf`. */
export function buildDeviceReportDocDefinition(
  payload: unknown,
  info: DeviceReportPdfInfo
): TDocumentDefinitions {
  const headerLine = [info.capturedAtLabel, info.overallResult]
    .filter((part) => part.length > 0)
    .join("   ·   ");

  return {
    pageSize: "A4",
    pageMargins: [32, 32, 32, 32],
    // The subject is the useful window title, and the id disambiguates two reports of one device.
    info: { title: buildDeviceReportFileName(info.subject, info.reportId, "") },
    content: [
      { text: info.labels.title, style: "documentTitle" },
      { text: info.subjectLabel, style: "subject" },
      ...(headerLine ? [{ text: headerLine, style: "meta" } as Content] : []),
      { text: info.reportId, style: "reportId" },
      ...buildStepsContent(toDisplaySteps(payload), info.labels),
      ...buildRawDataContent(toFlatReportEntries(payload), info.labels),
    ],
    styles: {
      documentTitle: { fontSize: 9, color: "#666666", margin: [0, 0, 0, 2] },
      subject: { fontSize: 16, bold: true },
      meta: { fontSize: 10, margin: [0, 2, 0, 0] },
      reportId: { fontSize: 8, color: "#666666", margin: [0, 2, 0, 12] },
      sectionTitle: { fontSize: 12, bold: true, margin: [0, 8, 0, 4] },
      tableHeader: { bold: true, fontSize: 9 },
      table: { fontSize: 8 },
    },
    defaultStyle: { fontSize: 9 },
  };
}

/** Saves the payload as the JSON it was stored as. */
export function downloadDeviceReportJson(
  payload: unknown,
  subject: string,
  reportId: string
): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json;charset=utf-8",
  });

  saveAs(blob, buildDeviceReportFileName(subject, reportId, ".json"));
}

/** Renders the payload to a PDF and saves it. */
export function downloadDeviceReportPdf(payload: unknown, info: DeviceReportPdfInfo): void {
  pdfMake
    .createPdf(buildDeviceReportDocDefinition(payload, info))
    .download(buildDeviceReportFileName(info.subject, info.reportId, ".pdf"));
}
